import { useIntl } from 'react-intl';
import { useMemo } from 'react';

import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { ConsolidatedForecastReportTab } from '../../../../../core/types';
import { getSystemAssumptionDisplayName, getTopLevelRowDisplayName } from './utils';
import { NodeStaticId } from '../../../../../forecast/prediction-builder/types';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';

export const useOrganisationAccountNames = (tab?: ConsolidatedForecastReportTab) => {
  const { forecastReportV2: forecastReport } = usePredictionsInfoContext();
  const { nodeStaticIds, accountStaticIdOverrides } = useLocalisedConstantsContext();
  const intl = useIntl();

  const index = useMemo(() => {
    const index: Record<string, string> = {};
    const tabOverrideStaticIds: Record<string, string> = {};

    // Nodes within the tab can have override static IDs set, which is done
    // to allow displaying a different label for that account
    tab?.children.forEach(child => {
      const staticIdOverride = child.override_static_id as NodeStaticId;
      if (staticIdOverride && accountStaticIdOverrides[staticIdOverride]) {
        tabOverrideStaticIds[child.ref] = accountStaticIdOverrides[staticIdOverride];
      }
    });

    if (forecastReport) {
      // Run through all nodes and create account name index
      Object.entries(forecastReport.node_info.by_ref).forEach(([ref, node]) => {
        let name = node.name || '';

        // Override default node name for specific node types
        if (tabOverrideStaticIds[ref]) {
          name = tabOverrideStaticIds[ref];
        } else if (node.type === 'top-level-static' || node.type === 'total') {
          name = getTopLevelRowDisplayName(nodeStaticIds, node);
        } else if (node.type === 'system-assumption') {
          name = getSystemAssumptionDisplayName(intl, node);
        }

        index[ref] = name;
      });
    }

    return index;
  }, [accountStaticIdOverrides, forecastReport, intl, nodeStaticIds, tab?.children]);

  return index;
};
